import React, { useContext } from 'react';
import { COLORS, FONT, RADIUS } from './tokens';
import { MenuContext, findCrumb } from './menuCrumb';
import useIsMobile from '../../../helper/useIsMobile';

/*
 * PageHeader — THE headline of every admin screen, form and report (2026-10-22).
 *
 *   SETUP · DEVICES                              <- breadcrumb (from the menu, automatic)
 *   ▌ Add Device                     [actions]   <- title + optional right-hand actions
 *     Register a device type for the catalogue   <- optional subtitle
 *
 * Replaces the <h4>+<hr> form titles and the red-rule "legend" titles on reports, which
 * read as stray lines rather than headings. No rule is drawn across the page.
 *
 * The breadcrumb is derived from the current route and the user's menu, so no screen
 * hard-codes it: "Section · Menu item", shortened to just "Section" when the title already
 * is the menu item (a report called "Warehouse Balance" under Report shows "REPORT").
 * Pass `breadcrumb` (string or array) to override, `breadcrumb="section"` for the section alone
 * (reports, whose title already names the report), or `breadcrumb={false}` to hide it.
 */
const TAG = {
    new:  { label: 'New',     bg: COLORS.infoBg,    fg: COLORS.infoText },
    edit: { label: 'Editing', bg: COLORS.warningBg, fg: COLORS.warningText },
};

/* mode: 'new' | 'edit' adds a small "New" / "Editing" tag after the title (add/edit forms). */
export default function PageHeader({ title, subtitle, breadcrumb, right, mode, style }) {
    const tag = TAG[mode];
    const sections = useContext(MenuContext);
    const isMobile = useIsMobile();   // phone: smaller title, actions wrap under it

    let crumbs = [];
    if (Array.isArray(breadcrumb)) crumbs = breadcrumb;
    else if (typeof breadcrumb === 'string' && breadcrumb !== 'section') crumbs = [breadcrumb];
    else if (breadcrumb !== false) {
        const c = findCrumb(sections, typeof window !== 'undefined' ? window.location.pathname : '');
        if (c && breadcrumb === 'section') crumbs = [c.section];
        else if (c) {
            const same = typeof title === 'string' && title.trim().toLowerCase() === String(c.text).trim().toLowerCase();
            crumbs = same ? [c.section] : [c.section, c.text];
        }
    }

    return (
        <div style={{
            display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 16, margin: '20px 0 16px', ...style,
        }}>
            <div style={{ minWidth: 0 }}>
                {crumbs.length > 0 && (
                    <div style={{
                        fontFamily: FONT, fontSize: 11, fontWeight: 600, letterSpacing: '0.08em',
                        textTransform: 'uppercase', color: COLORS.textDim, marginBottom: 6,
                    }}>
                        {crumbs.join('  ·  ')}
                    </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
                    <span aria-hidden="true" style={{
                        width: 4, height: 24, borderRadius: 4, background: COLORS.primary, flexShrink: 0,
                    }} />
                    <h1 style={{
                        margin: 0, fontFamily: FONT, fontSize: isMobile ? 18 : 20, fontWeight: 700,
                        color: COLORS.text, lineHeight: 1.25,
                    }}>
                        {title}
                    </h1>
                    {tag && (
                        <span style={{
                            fontFamily: FONT, fontSize: 11, fontWeight: 600, padding: '2px 8px',
                            borderRadius: RADIUS.chip, background: tag.bg, color: tag.fg, letterSpacing: '0.02em',
                        }}>{tag.label}</span>
                    )}
                </div>
                {subtitle && (
                    <p style={{ margin: '6px 0 0 16px', fontFamily: FONT, fontSize: 13, color: COLORS.textDim }}>
                        {subtitle}
                    </p>
                )}
            </div>
            {right && <div style={isMobile
                ? { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }
                : { display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>{right}</div>}
        </div>
    );
}
